/**
 * K-T-12 — Layer-3 hook for canonical per-technician efficiency
 * within a chosen 5-year vehicle-year bucket (drill-down sibling of
 * K-T-11's `useVehicleYearGroupEfficiency`).
 *
 * Thin wrapper around `kpi_efficiency_by_vehicle_year_group_tech(
 *   shop_id, start_at, end_at, year_start, year_end)`. Returns one
 * `DrilldownTechEfficiencyResult` per technician with both display
 * totals and the canonical-filtered ratio + sample-gate fields.
 * Holds NO local KPI math.
 *
 * The hook accepts the displayed bucket label (e.g. `'2016–2020'`) and
 * parses it locally via `split('–')` — single source of truth for the
 * parse, mirroring the pre-K-T-12 panel logic. The RPC takes parsed
 * integers to keep the SQL simple (no Unicode delimiter handling).
 *
 * Gated by `yearGroup`: hook is disabled when null/undefined or
 * un-parseable.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { fromZonedTime } from 'date-fns-tz';
import type { DrilldownTechEfficiencyResult } from '../../../supabase/functions/_shared/kpiCanonical';

export interface UseVehicleYearGroupTechEfficiencyOptions {
  shopId: string;
  /** Shop-local `yyyy-MM-dd`. */
  startDate: string;
  /** Shop-local `yyyy-MM-dd`. */
  endDate: string;
  shopTimezone: string;
  /**
   * The expanded year-bucket label (e.g. `'2016–2020'`, en dash
   * separator). Hook is disabled when null/undefined.
   */
  yearGroup: string | null | undefined;
}

export interface UseVehicleYearGroupTechEfficiencyResult {
  data: DrilldownTechEfficiencyResult[] | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * Parse a `'YYYY–YYYY'` (en dash) bucket label into start/end ints.
 * Returns `null` when the label is missing or un-parseable.
 */
function parseBucket(
  label: string | null | undefined,
): { start: number; end: number } | null {
  if (!label) return null;
  const parts = label.split('–');
  if (parts.length !== 2) return null;
  const start = parseInt(parts[0], 10);
  const end = parseInt(parts[1], 10);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return { start, end };
}

/**
 * Stable query key:
 *   ['kpi', 'kpi_efficiency_by_vehicle_year_group_tech', shopId,
 *    startDate, endDate, shopTimezone, yearGroup]
 */
export function useVehicleYearGroupTechEfficiency(
  opts: UseVehicleYearGroupTechEfficiencyOptions,
): UseVehicleYearGroupTechEfficiencyResult {
  const parsed = parseBucket(opts.yearGroup);

  const query = useQuery({
    queryKey: [
      'kpi',
      'kpi_efficiency_by_vehicle_year_group_tech',
      opts.shopId,
      opts.startDate,
      opts.endDate,
      opts.shopTimezone,
      opts.yearGroup,
    ],
    queryFn: async (): Promise<DrilldownTechEfficiencyResult[] | null> => {
      const startAt = fromZonedTime(
        `${opts.startDate}T00:00:00`,
        opts.shopTimezone,
      ).toISOString();
      const endAt = fromZonedTime(
        `${opts.endDate}T23:59:59.999`,
        opts.shopTimezone,
      ).toISOString();

      const { data, error } = await (supabase.rpc as unknown as (
        fn: string,
        args: {
          p_shop_id: string;
          p_start_at: string;
          p_end_at: string;
          p_year_start: number;
          p_year_end: number;
        },
      ) => Promise<{
        data: DrilldownTechEfficiencyResult[] | null;
        error: unknown;
      }>)('kpi_efficiency_by_vehicle_year_group_tech', {
        p_shop_id: opts.shopId,
        p_start_at: startAt,
        p_end_at: endAt,
        p_year_start: (parsed as { start: number; end: number }).start,
        p_year_end: (parsed as { start: number; end: number }).end,
      });

      if (error) throw error;
      return data ?? [];
    },
    enabled: Boolean(
      opts.shopId &&
        opts.startDate &&
        opts.endDate &&
        opts.shopTimezone &&
        parsed,
    ),
    staleTime: 5 * 60 * 1000,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
